import React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { http } from "@/lib/http";
import { useToast } from "@/hooks/use-toast";

const FEEDBACK_REASON_CODES = [
  "BRAND_NOT_VISIBLE",
  "STOCK_NOT_VISIBLE",
  "POS_NOT_VISIBLE",
  "IMAGE_QUALITY_POOR",
] as const;

type FeedbackReasonCode = typeof FEEDBACK_REASON_CODES[number];
type ActionMode = "dismiss" | "should-have-flagged";

interface TriageResult {
  id: number;
  flagged: boolean;
  reasons: string[] | null;
  confidence: number;
  observations?: {
    brand_visible?: boolean | null;
    stock_visible?: boolean | null;
    pos_visible?: boolean | null;
    expected_format_match?: "match" | "mismatch" | "unclear" | null;
    expected_format_reason?: string;
    pos_removed_confirmed?: boolean | null;
    residual_material_visible?: boolean | null;
    floor_graphic_residue_visible?: boolean | null;
    floor_graphic_edge_or_fragment_visible?: boolean | null;
    target_area_fully_visible?: boolean | null;
    image_quality?: "acceptable" | "poor" | "unclear" | string;
    instruction_fit?: "likely_met" | "likely_unmet" | "unclear" | "not_applicable" | string;
    instruction_fit_reason?: string;
    notes?: string;
  } | null;
  computedVerdict?: {
    verdict: "PASS" | "PARTIAL_FAIL" | "FAIL";
    reasons: string[];
    reasonCodes: string[];
  } | null;
  createdAt: string;
}

interface LatestTriageResponse {
  ok: boolean;
  result: TriageResult | null;
  job?: {
    id: number;
    status: string;
    attempts: number;
    maxAttempts: number;
    lastError: string | null;
    startedAt: string | null;
    processedAt: string | null;
    createdAt: string | null;
  } | null;
  evaluation?: {
    id: number;
    aiTriageResultId: number | null;
    evaluationKind: string;
    humanDecision: string;
    disagreementType: string | null;
    humanReasonCodes: string[] | null;
    notes?: string | null;
    agreement: boolean;
    createdAt: string;
  } | null;
}

interface AiTriageFeedbackPanelProps {
  submissionId: number;
  taskType?: string | null;
  reviewerDecision?: "APPROVE" | "REJECT";
  canView: boolean;
  canRun: boolean;
  /** When true, fetch latest from review API (reviewer-accessible) instead of admin API. */
  useReviewApi?: boolean;
}

export function AiTriageFeedbackPanel({
  submissionId,
  taskType,
  reviewerDecision,
  canView,
  canRun,
  useReviewApi = false,
}: AiTriageFeedbackPanelProps) {
  const { toast } = useToast();
  const [actionMode, setActionMode] = React.useState<ActionMode | null>(null);
  const [selectedReasonCodes, setSelectedReasonCodes] = React.useState<FeedbackReasonCode[]>([]);
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = React.useState(false);
  const [runRequested, setRunRequested] = React.useState(false);
  const [feedbackNote, setFeedbackNote] = React.useState("");

  const isRemovalTask = (taskType || "").toUpperCase() === "REMOVAL";

  const latestUrl = useReviewApi
    ? `/api/review/submissions/${submissionId}/ai-triage-latest`
    : `/api/admin/ai-triage/latest?submissionId=${submissionId}`;
  const latestQueryKey = useReviewApi
    ? ["/api/review/submissions", submissionId, "ai-triage-latest"]
    : ["/api/admin/ai-triage/latest", submissionId];

  const latestQuery = useQuery<LatestTriageResponse | null>({
    queryKey: latestQueryKey,
    enabled: canView && submissionId > 0,
    refetchInterval: runRequested ? 5000 : false,
    queryFn: async () => {
      const response = await http(latestUrl);
      if (response.status === 401 || response.status === 403) {
        return null;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch latest AI triage result");
      }
      return response.json();
    },
  });

  const runMutation = useMutation({
    mutationFn: async (options?: { retry?: boolean }) => {
      const response = await http("/api/admin/ai-triage/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submissionId,
          retry: options?.retry === true,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to enqueue AI check");
      }
      return payload;
    },
    onSuccess: (payload: { enqueued?: boolean }, variables) => {
      if (payload?.enqueued) {
        setRunRequested(true);
      }
      latestQuery.refetch();
      toast({
        title: "AI check requested",
        description: payload?.enqueued
          ? (variables?.retry ? "Retry enqueued for AI triage." : "Submission enqueued for AI triage.")
          : "AI triage could not be re-queued right now.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "AI check failed", description: error.message, variant: "destructive" });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      const response = await http("/api/admin/ai-triage/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submissionId,
          aiTriageResultId: triageResult?.id,
          note: feedbackNote.trim() || undefined,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to record feedback");
      }
      return payload;
    },
    onSuccess: () => {
      setHasSubmittedFeedback(true);
      setActionMode(null);
      setSelectedReasonCodes([]);
      void latestQuery.refetch();
      toast({ title: "Feedback recorded", description: "Agreement feedback saved." });
    },
    onError: (error: Error) => {
      toast({ title: "Feedback failed", description: error.message, variant: "destructive" });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async (reasonCodes: FeedbackReasonCode[]) => {
      const response = await http("/api/admin/ai-triage/dismiss", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ submissionId, reasonCodes, note: feedbackNote.trim() || undefined }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to record feedback");
      }
      return payload;
    },
    onSuccess: () => {
      setHasSubmittedFeedback(true);
      setActionMode(null);
      setSelectedReasonCodes([]);
      void latestQuery.refetch();
      toast({ title: "Feedback recorded", description: "Disagreement feedback saved." });
    },
    onError: (error: Error) => {
      toast({ title: "Feedback failed", description: error.message, variant: "destructive" });
    },
  });

  const shouldHaveFlaggedMutation = useMutation({
    mutationFn: async (reasonCodes: FeedbackReasonCode[]) => {
      const response = await http("/api/admin/ai-triage/should-have-flagged", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ submissionId, reasonCodes, note: feedbackNote.trim() || undefined }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to record feedback");
      }
      return payload;
    },
    onSuccess: () => {
      setHasSubmittedFeedback(true);
      setActionMode(null);
      setSelectedReasonCodes([]);
      void latestQuery.refetch();
      toast({ title: "Feedback recorded", description: "Disagreement feedback saved." });
    },
    onError: (error: Error) => {
      toast({ title: "Feedback failed", description: error.message, variant: "destructive" });
    },
  });

  React.useEffect(() => {
    setActionMode(null);
    setSelectedReasonCodes([]);
    setHasSubmittedFeedback(false);
    setRunRequested(false);
    setFeedbackNote("");
  }, [submissionId]);

  React.useEffect(() => {
    if (latestQuery.data?.result) {
      setRunRequested(false);
    }
    if (latestQuery.data?.job?.status === "failed") {
      setRunRequested(false);
    }
  }, [latestQuery.data?.result, latestQuery.data?.job?.status]);

  React.useEffect(() => {
    if (typeof latestQuery.data?.evaluation?.notes === "string") {
      setFeedbackNote(latestQuery.data.evaluation.notes);
    }
  }, [latestQuery.data?.evaluation?.id, latestQuery.data?.evaluation?.notes]);

  if (!canView) {
    return null;
  }

  const triageResult = latestQuery.data?.result ?? null;
  const triageJob = latestQuery.data?.job ?? null;
  const triageEvaluation = latestQuery.data?.evaluation ?? null;
  const computedVerdict = triageResult?.computedVerdict ?? null;
  const persistedFeedbackChoice = triageEvaluation
    ? (triageEvaluation.agreement ? "agree" : "disagree")
    : null;
  const isSubmitting =
    runMutation.isPending ||
    confirmMutation.isPending ||
    dismissMutation.isPending ||
    shouldHaveFlaggedMutation.isPending;

  const toggleReasonCode = (reasonCode: FeedbackReasonCode) => {
    setSelectedReasonCodes((prev) =>
      prev.includes(reasonCode)
        ? prev.filter((code) => code !== reasonCode)
        : [...prev, reasonCode],
    );
  };

  const submitReasonedAction = () => {
    if (selectedReasonCodes.length === 0) {
      toast({
        title: "Reason required",
        description: "Select at least one reason code before saving.",
        variant: "destructive",
      });
      return;
    }

    if (actionMode === "dismiss") {
      dismissMutation.mutate(selectedReasonCodes);
      return;
    }

    if (actionMode === "should-have-flagged") {
      shouldHaveFlaggedMutation.mutate(selectedReasonCodes);
    }
  };

  const deriveReasonCodes = (): FeedbackReasonCode[] => {
    const fromVerdict = (computedVerdict?.reasonCodes || [])
      .filter((code): code is FeedbackReasonCode => FEEDBACK_REASON_CODES.includes(code as FeedbackReasonCode));

    if (fromVerdict.length > 0) {
      return Array.from(new Set(fromVerdict));
    }

    const derived: FeedbackReasonCode[] = [];
    const observations = triageResult?.observations;
    if (!observations) {
      return derived;
    }

    if (observations.brand_visible !== true) {
      derived.push("BRAND_NOT_VISIBLE");
    }
    if (observations.pos_visible !== true) {
      derived.push("POS_NOT_VISIBLE");
    }
    if (observations.stock_visible !== true) {
      derived.push("STOCK_NOT_VISIBLE");
    }
    if (observations.image_quality === "poor") {
      derived.push("IMAGE_QUALITY_POOR");
    }

    return Array.from(new Set(derived));
  };

  const submitDisagreement = () => {
    if (!triageResult || !computedVerdict) {
      return;
    }

    if (!reviewerDecision) {
      toast({
        title: "Choose decision first",
        description: "Select Approve or Reject first, then submit disagreement.",
        variant: "destructive",
      });
      return;
    }

    let targetAction: ActionMode | null = null;
    if ((computedVerdict.verdict === "FAIL" || computedVerdict.verdict === "PARTIAL_FAIL") && reviewerDecision === "APPROVE") {
      targetAction = "dismiss";
    } else if (computedVerdict.verdict === "PASS" && reviewerDecision === "REJECT") {
      targetAction = "should-have-flagged";
    }

    if (!targetAction) {
      toast({
        title: "No disagreement mapping needed",
        description: "Your current decision aligns with the AI verdict.",
      });
      return;
    }

    const reasonCodes = deriveReasonCodes();
    if (reasonCodes.length === 0) {
      setActionMode(targetAction);
      setSelectedReasonCodes([]);
      return;
    }

    if (targetAction === "dismiss") {
      dismissMutation.mutate(reasonCodes);
      return;
    }

    shouldHaveFlaggedMutation.mutate(reasonCodes);
  };

  const formatBool = (value: boolean | null | undefined): string => {
    if (value === true) return "Visible";
    if (value === false) return "Not visible";
    return "Unclear";
  };

  const formatQuality = (value: string | undefined): string => {
    if (value === "acceptable") return "Acceptable";
    if (value === "poor") return "Poor";
    return "Unclear";
  };

  const formatInstructionFit = (value: string | undefined): string => {
    if (value === "likely_met") return "Likely Met";
    if (value === "likely_unmet") return "Likely Unmet";
    if (value === "not_applicable") return "N/A";
    return "Unclear";
  };

  const formatVerdict = (value: string | undefined): string => {
    if (value === "PASS") return "PASS";
    if (value === "FAIL") return "FAIL";
    if (value === "PARTIAL_FAIL") return "PARTIAL FAIL";
    return "Pending";
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm">AI Triage</CardTitle>
          {hasSubmittedFeedback && (
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
              Feedback Recorded
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {latestQuery.isLoading && !runRequested ? (
          <p className="text-xs text-muted-foreground">Loading AI check...</p>
        ) : !triageResult ? (
          <p className="text-xs text-muted-foreground">
            {isRemovalTask
              ? "Run AI check to get a quick removal-compliance signal for this submission."
              : "Run AI check to get a quick quality signal for this submission."}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            {isRemovalTask
              ? "AI verdict is computed from full-removal checks (including residue/glue traces)."
              : "AI verdict is computed from required checks."}
          </p>
        )}

        {latestQuery.isError && (
          <p className="text-xs text-red-600">Failed to load triage state. Review flow is unaffected.</p>
        )}

        {triageJob && (
          <div className="text-xs text-muted-foreground space-y-1 rounded-md border bg-muted/20 p-2">
            <p>
              Job: <span className="font-medium text-foreground">{triageJob.status}</span>
              {" "}
              <span>({triageJob.attempts}/{triageJob.maxAttempts})</span>
            </p>
            {triageJob.status === "failed" && triageJob.lastError && (
              <p>
                Error: <span className="font-medium text-foreground">{triageJob.lastError}</span>
              </p>
            )}
          </div>
        )}

        {triageResult && (
          <div className="text-xs text-muted-foreground space-y-1">
            <p>
              AI Verdict:{" "}
              <span className="font-medium text-foreground">
                {formatVerdict(computedVerdict?.verdict)}
              </span>
            </p>
            {computedVerdict?.reasons?.length ? (
              <div>
                <p className="font-medium text-foreground">Why:</p>
                <ul className="list-disc pl-4">
                  {computedVerdict.reasons.slice(0, 4).map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {Array.isArray(triageResult.reasons) && triageResult.reasons.length > 0 && (
              <p>
                Reasons: <span className="font-medium text-foreground">{triageResult.reasons.join(", ")}</span>
              </p>
            )}
            {triageResult.observations && (
              <div className="pt-1">
                {isRemovalTask ? (
                  <>
                    <p>All media fully removed: <span className="font-medium text-foreground">{formatBool(triageResult.observations.pos_removed_confirmed)}</span></p>
                    <p>Residual material visible: <span className="font-medium text-foreground">{formatBool(triageResult.observations.residual_material_visible)}</span></p>
                    <p>Floor residue/ghosting visible: <span className="font-medium text-foreground">{formatBool(triageResult.observations.floor_graphic_residue_visible)}</span></p>
                    <p>Floor edge/fragment visible: <span className="font-medium text-foreground">{formatBool(triageResult.observations.floor_graphic_edge_or_fragment_visible)}</span></p>
                    <p>Target area fully visible: <span className="font-medium text-foreground">{formatBool(triageResult.observations.target_area_fully_visible)}</span></p>
                    <p>Image quality: <span className="font-medium text-foreground">{formatQuality(triageResult.observations.image_quality)}</span></p>
                  </>
                ) : (
                  <>
                    <p>Brand: <span className="font-medium text-foreground">{formatBool(triageResult.observations.brand_visible)}</span></p>
                    <p className="text-[11px] text-muted-foreground">
                      Brand visible means the branded ad/POS or clearly branded product proof is visible in this photo.
                    </p>
                    <p>Stock: <span className="font-medium text-foreground">{formatBool(triageResult.observations.stock_visible)}</span></p>
                    <p>POS: <span className="font-medium text-foreground">{formatBool(triageResult.observations.pos_visible)}</span></p>
                    <p className="text-[11px] text-muted-foreground">
                      POS visible means the installed media itself is visible (for example floor vinyl, fridge vinyl, shelf strip, wobblers, poster, or other branded placement).
                    </p>
                    {triageResult.observations.expected_format_match && (
                      <p>
                        Expected format match:{" "}
                        <span className="font-medium text-foreground">
                          {triageResult.observations.expected_format_match === "match"
                            ? "Match"
                            : triageResult.observations.expected_format_match === "mismatch"
                              ? "Mismatch"
                              : "Unclear"}
                        </span>
                      </p>
                    )}
                    {triageResult.observations.expected_format_reason && (
                      <p>Format reasoning: <span className="font-medium text-foreground">{triageResult.observations.expected_format_reason}</span></p>
                    )}
                    <p>Image quality: <span className="font-medium text-foreground">{formatQuality(triageResult.observations.image_quality)}</span></p>
                    <p>Posting instructions fit: <span className="font-medium text-foreground">{formatInstructionFit(triageResult.observations.instruction_fit)}</span></p>
                    <p className="text-[11px] text-muted-foreground">
                      Posting instructions fit means the visible placement appears to match the requested bay/location and setup in the posting instructions.
                    </p>
                    {triageResult.observations.instruction_fit_reason && (
                      <p>Why: <span className="font-medium text-foreground">{triageResult.observations.instruction_fit_reason}</span></p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {triageEvaluation && (
          <div className="text-xs text-muted-foreground space-y-1 rounded-md border bg-muted/20 p-2">
            <p>
              Feedback:{" "}
              <span className="font-medium text-foreground">
                {triageEvaluation.humanDecision === "ATTENTION_CONFIRMED" ? "Sensible" : "Not Sensible"}
              </span>
            </p>
            {Array.isArray(triageEvaluation.humanReasonCodes) && triageEvaluation.humanReasonCodes.length > 0 && (
              <p>
                Reason codes:{" "}
                <span className="font-medium text-foreground">{triageEvaluation.humanReasonCodes.join(", ")}</span>
              </p>
            )}
            {typeof triageEvaluation.notes === "string" && triageEvaluation.notes.trim().length > 0 && (
              <p>
                Note:{" "}
                <span className="font-medium text-foreground">{triageEvaluation.notes}</span>
              </p>
            )}
          </div>
        )}

        {triageResult && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-foreground">AI Feedback Note (Optional)</p>
            <Textarea
              rows={2}
              value={feedbackNote}
              onChange={(e) => setFeedbackNote(e.target.value)}
              placeholder="Add context for why you agree or disagree with the AI verdict"
              disabled={hasSubmittedFeedback || isSubmitting || !!triageEvaluation}
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {!triageResult ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!canRun || hasSubmittedFeedback || isSubmitting || !!triageEvaluation}
                onClick={() => runMutation.mutate(undefined)}
              >
                {triageJob?.status === "failed" ? "Retry AI Check" : "Run AI Check"}
              </Button>
              {runRequested && (
                <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                  Awaiting Result
                </Badge>
              )}
            </>
          ) : (
            <>
              <p className="w-full text-xs text-muted-foreground">Do you agree with this AI verdict?</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={hasSubmittedFeedback || isSubmitting || !!triageEvaluation}
                onClick={() => {
                  confirmMutation.mutate();
                }}
              >
                {persistedFeedbackChoice === "agree" ? "Agreed" : "Agree"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={hasSubmittedFeedback || isSubmitting || !!triageEvaluation}
                onClick={submitDisagreement}
              >
                {persistedFeedbackChoice === "disagree" ? "Disagreed" : "Disagree"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!canRun || isSubmitting || runMutation.isPending}
                onClick={() => runMutation.mutate({ retry: true })}
              >
                Retry AI
              </Button>
            </>
          )}
        </div>

        {actionMode && !hasSubmittedFeedback && (
          <div className="rounded-md border bg-muted/20 p-3 space-y-3">
            <p className="text-xs font-medium">
              Select reason code(s)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FEEDBACK_REASON_CODES.map((reasonCode) => (
                <label key={reasonCode} className="flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={selectedReasonCodes.includes(reasonCode)}
                    onCheckedChange={() => toggleReasonCode(reasonCode)}
                  />
                  <span>{reasonCode}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" onClick={submitReasonedAction} disabled={isSubmitting}>
                Save Feedback
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setActionMode(null);
                  setSelectedReasonCodes([]);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AiTriageFeedbackPanel;
